import { useEffect, useRef, useState, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CountryProvider } from "@/context/CountryContext";
import { SiteFlagsProvider, FlagRoute } from "@/context/SiteFlagsContext";
import api from "@/lib/api";
import { Loader } from "@/components/Loader";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HealthBanner } from "@/components/HealthBanner";
// El hub flotante (asistente IA + asesores) se monta diferido (tras idle) y fuera del bundle inicial.
const FloatingHub = lazyWithReload(() =>
  import("@/components/FloatingHub").then((m) => ({ default: m.FloatingHub })));
import { installGlobalErrorReporting } from "@/lib/errorReporter";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { PushBootstrap } from "@/components/PushBootstrap";

import Landing from "@/pages/Landing";

// Code-splitting: el resto de páginas se cargan bajo demanda para acelerar la carga inicial.
// lazyWithReload se auto-recupera de ChunkLoadError tras un nuevo despliegue.
const Login = lazyWithReload(() => import("@/pages/Login"));
const AdminLogin = lazyWithReload(() => import("@/pages/AdminLogin"));
const Plans = lazyWithReload(() => import("@/pages/Plans"));
const VerifyReport = lazyWithReload(() => import("@/pages/VerifyReport"));
const VerifyFolio = lazyWithReload(() => import("@/pages/VerifyFolio"));
const InformeExpress = lazyWithReload(() => import("@/pages/InformeExpress"));
const Comparador = lazyWithReload(() => import("@/components/pending-services/Comparador"));
const PublicShowroom = lazyWithReload(() => import("@/pages/PublicShowroom"));
const Vitrinas = lazyWithReload(() => import("@/components/pending-services/Vitrinas"));
const Portal = lazyWithReload(() => import("@/pages/Portal"));
const AutomotorasLanding = lazyWithReload(() => import("@/components/pending-services/AutomotorasLanding"));
const OnePager = lazyWithReload(() => import("@/pages/OnePager"));
const QuienesSomos = lazyWithReload(() => import("@/pages/QuienesSomos"));
const PaisLanding = lazyWithReload(() => import("@/pages/PaisLanding"));
const Verificacion = lazyWithReload(() => import("@/components/pending-services/Verificacion"));
const VerificacionConsola = lazyWithReload(() => import("@/components/pending-services/VerificacionConsola"));
const VerificarInforme = lazyWithReload(() => import("@/pages/VerificarInforme"));
const ConsentimientoCMF = lazyWithReload(() => import("@/pages/ConsentimientoCMF"));
const LeadResponder = lazyWithReload(() => import("@/pages/LeadResponder"));
const ValidarLead = lazyWithReload(() => import("@/pages/ValidarLead"));
const Onboarding = lazyWithReload(() => import("@/pages/Onboarding"));
const Dashboard = lazyWithReload(() => import("@/pages/Dashboard"));
const NewTransfer = lazyWithReload(() => import("@/pages/NewTransfer"));
const OperationDetail = lazyWithReload(() => import("@/pages/OperationDetail"));
const Informe360 = lazyWithReload(() => import("@/pages/Informe360"));
const Payment = lazyWithReload(() => import("@/pages/Payment"));
const PaymentChoice = lazyWithReload(() => import("@/pages/PaymentChoice"));
const NotaryPanel = lazyWithReload(() => import("@/pages/NotaryPanel"));
const AutomotoraPanel = lazyWithReload(() => import("@/pages/AutomotoraPanel"));
const AdminPanel = lazyWithReload(() => import("@/pages/AdminPanel"));
const Derechos = lazyWithReload(() => import("@/pages/Derechos"));
const PortalTitular = lazyWithReload(() => import("@/pages/PortalTitular"));
const Firmas = lazyWithReload(() => import("@/pages/Firmas"));
const FirmarDocumento = lazyWithReload(() => import("@/pages/FirmarDocumento"));
const VerificarFirma = lazyWithReload(() => import("@/pages/VerificarFirma"));
const PitchDeck = lazyWithReload(() => import("@/pages/PitchDeck"));
const PitchInversionistas = lazyWithReload(() => import("@/pages/PitchInversionistas"));
const CookiesBanner = lazyWithReload(() => import("@/components/CookiesBanner").then((m) => ({ default: m.CookiesBanner })));
const SelloPublico = lazyWithReload(() => import("@/pages/SelloPublico"));
const PagoProtegido = lazyWithReload(() => import("@/components/pending-services/PagoProtegido"));
const SelloGarantizado = lazyWithReload(() => import("@/components/pending-services/SelloGarantizado"));
const InspeccionMovil = lazyWithReload(() => import("@/components/pending-services/InspeccionMovil"));
const InspeccionPago = lazyWithReload(() => import("@/pages/InspeccionPago"));
const InspectorInforme = lazyWithReload(() => import("@/pages/InspectorInforme"));
const InspeccionInforme = lazyWithReload(() => import("@/pages/InspeccionInforme"));
const InspeccionReagendar = lazyWithReload(() => import("@/pages/InspeccionReagendar"));
const MisInspecciones = lazyWithReload(() => import("@/pages/MisInspecciones"));
const VendePasivo = lazyWithReload(() => import("@/components/pending-services/VendePasivo"));
const ApiIntelligence = lazyWithReload(() => import("@/pages/ApiIntelligence"));
const Precios = lazyWithReload(() => import("@/pages/Precios"));
const AceptarInvitacion = lazyWithReload(() => import("@/pages/AceptarInvitacion"));
const TasarMiAuto = lazyWithReload(() => import("@/pages/TasarMiAuto"));
const ReferralLanding = lazyWithReload(() => import("@/pages/ReferralLanding"));
const CuantoVale = lazyWithReload(() => import("@/pages/CuantoVale"));
const CuantoValeModelo = lazyWithReload(() => import("@/pages/CuantoValeModelo"));
const PoliticaPrivacidad = lazyWithReload(() => import("@/pages/PoliticaPrivacidad"));
const PublicNotarias = lazyWithReload(() => import("@/components/pending-services/PublicNotarias"));
const ComoOperamos = lazyWithReload(() => import("@/pages/ComoOperamos"));
const CotizacionPublica = lazyWithReload(() => import("@/pages/CotizacionPublica"));
const ComparativoPublico = lazyWithReload(() => import("@/pages/ComparativoPublico"));
const Escanear = lazyWithReload(() => import("@/components/pending-services/Escanear"));
const PublicTalleres = lazyWithReload(() => import("@/components/pending-services/PublicTalleres"));
const TallerPanel = lazyWithReload(() => import("@/pages/TallerPanel"));
const CertVerify = lazyWithReload(() => import("@/pages/CertVerify"));
const PrecompraPublica = lazyWithReload(() => import("@/pages/PrecompraPublica"));
const PrecompraPublicoCalcular = lazyWithReload(() => import("@/pages/PrecompraPublicoCalcular"));
const Vendedores = lazyWithReload(() => import("@/pages/Vendedores"));

function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login");
      return;
    }
    api
      .post("/api/auth/session", { session_id: sid })
      .then(async ({ data }) => {
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        navigate(data.role ? "/dashboard" : "/onboarding", { replace: true });
      })
      .catch(() => navigate("/login"));
  }, [navigate, refresh]);

  return <Loader label="Validando tu identidad…" />;
}

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const adminRoute = roles && roles.includes("admin");
  if (loading) return <Loader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={adminRoute ? "/admin/login" : `/login?next=${next}`} replace />;
  }
  if (!user.role && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={adminRoute ? "/admin/login" : "/dashboard"} replace />;
  return children;
}

function AppRouter() {
  // Prefetch en reposo: descarga los chunks de las secciones más usadas en segundo
  // plano para que el clic del usuario abra la página al instante (sin "Cargando…").
  useEffect(() => {
    const prefetch = () => {
      import("@/components/pending-services/Verificacion");
      import("@/pages/InformeExpress");
      import("@/pages/Login");
      import("@/pages/Payment");
      import("@/pages/Plans");
      import("@/pages/Portal");
      import("@/pages/Firmas");
      import("@/components/pending-services/Comparador");
      import("@/pages/VerificarInforme");
      import("@/pages/QuienesSomos");
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(prefetch, { timeout: 4000 });
    else setTimeout(prefetch, 2500);
  }, []);

  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/planes" element={<Plans />} />
      <Route path="/comparativa" element={<OnePager />} />
      <Route path="/quienes-somos" element={<QuienesSomos />} />
      <Route path="/ecuador" element={<PaisLanding code="EC" />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (centroVerificacion) */}
      <Route path="/verificacion" element={<FlagRoute name="centroVerificacion"><Verificacion /></FlagRoute>} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (centroVerificacion) */}
      <Route path="/verificacion/consola" element={<FlagRoute name="centroVerificacion"><RequireAuth><VerificacionConsola /></RequireAuth></FlagRoute>} />
      <Route path="/verificar-informe" element={<VerificarInforme />} />
      <Route path="/verificar-informe/:codigo" element={<VerificarInforme />} />
      <Route path="/consentimiento-cmf/:token" element={<ConsentimientoCMF />} />
      <Route path="/responder/:token" element={<LeadResponder />} />
      <Route path="/validar-lead/:token" element={<ValidarLead />} />
      <Route path="/verificar" element={<VerifyReport />} />
      <Route path="/verificar/:code" element={<VerifyReport />} />
      <Route path="/cotizacion/:code" element={<CotizacionPublica />} />
      <Route path="/comparativo/:code" element={<ComparativoPublico />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (escanearQr) */}
      <Route path="/escanear" element={<FlagRoute name="escanearQr"><Escanear /></FlagRoute>} />
      <Route path="/sello/:code" element={<SelloPublico />} />
      <Route path="/verificar-folio" element={<VerifyFolio />} />
      <Route path="/verificar-folio/:code" element={<VerifyFolio />} />
      <Route path="/informe-express/:code" element={<InformeExpress />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (compararPrecios) */}
      <Route path="/comparar" element={<FlagRoute name="compararPrecios"><Comparador /></FlagRoute>} />
      <Route path="/v/:slug" element={<PublicShowroom />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
      <Route path="/vitrinas" element={<FlagRoute name="enLaRedTodo"><Vitrinas /></FlagRoute>} />
      <Route path="/portal/:slug" element={<Portal />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
      <Route path="/automotoras" element={<FlagRoute name="enLaRedTodo"><AutomotorasLanding /></FlagRoute>} />
      <Route path="/aceptar-invitacion" element={<AceptarInvitacion />} />
      <Route path="/tasar-mi-auto" element={<TasarMiAuto />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/cuanto-vale" element={<CuantoVale />} />
      <Route path="/cuanto-vale/:marca/:modelo" element={<CuantoValeModelo />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (pagoProtegido) */}
      <Route path="/pago-protegido" element={<FlagRoute name="pagoProtegido"><PagoProtegido /></FlagRoute>} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (selloGarantizado) */}
      <Route path="/sello-garantizado" element={<FlagRoute name="selloGarantizado"><SelloGarantizado /></FlagRoute>} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (inspeccionMecanica) */}
      <Route path="/inspeccion-mecanica" element={<FlagRoute name="inspeccionMecanica"><InspeccionMovil /></FlagRoute>} />
      <Route path="/inspeccion-pago" element={<InspeccionPago />} />
      <Route path="/inspector-informe/:bookingId" element={<InspectorInforme />} />
      <Route path="/inspeccion-informe/:bookingId" element={<InspeccionInforme />} />
      <Route path="/inspeccion-reagendar/:bookingId" element={<InspeccionReagendar />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (ventaEn60Segundos) */}
      <Route path="/vende-en-60-segundos" element={<FlagRoute name="ventaEn60Segundos"><VendePasivo /></FlagRoute>} />
      <Route path="/inteligencia-b2b" element={<ApiIntelligence />} />
      <Route path="/precios" element={<Precios />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
      <Route path="/notarias" element={<FlagRoute name="enLaRedTodo"><PublicNotarias /></FlagRoute>} />
      <Route path="/como-operamos" element={<ComoOperamos />} />
      {/* servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
      <Route path="/talleres" element={<FlagRoute name="enLaRedTodo"><PublicTalleres /></FlagRoute>} />
      <Route path="/cert/:code" element={<CertVerify />} />
      <Route path="/precompra/:rid" element={<PrecompraPublica />} />
      <Route path="/precompra-publico/calcular" element={<PrecompraPublicoCalcular />} />
      <Route path="/vendedores" element={<Vendedores />} />
      <Route path="/taller" element={<RequireAuth roles={["taller", "admin"]}><TallerPanel /></RequireAuth>} />
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
      <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/nueva-transferencia" element={<RequireAuth><NewTransfer /></RequireAuth>} />
      <Route path="/operacion/:id" element={<RequireAuth><OperationDetail /></RequireAuth>} />
      <Route path="/operacion/:id/informe" element={<RequireAuth><Informe360 /></RequireAuth>} />
      <Route path="/derechos" element={<RequireAuth><Derechos /></RequireAuth>} />
      <Route path="/titular" element={<RequireAuth><PortalTitular /></RequireAuth>} />
      <Route path="/firmas" element={<RequireAuth><Firmas /></RequireAuth>} />
      <Route path="/firmar/:token" element={<FirmarDocumento />} />
      <Route path="/verificar-firma/:codigo" element={<VerificarFirma />} />
      <Route path="/pitch/:pais" element={<PitchDeck />} />
      <Route path="/inversionistas" element={<PitchInversionistas />} />
      <Route path="/mis-inspecciones" element={<RequireAuth><MisInspecciones /></RequireAuth>} />
      <Route path="/pago" element={<RequireAuth><Payment /></RequireAuth>} />
      <Route path="/elegir-pago" element={<RequireAuth><PaymentChoice /></RequireAuth>} />
      <Route path="/notaria" element={<RequireAuth roles={["notaria", "admin"]}><NotaryPanel /></RequireAuth>} />
      <Route path="/automotora" element={<RequireAuth roles={["automotora", "admin"]}><AutomotoraPanel /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth roles={["admin", "operador"]}><AdminPanel /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    installGlobalErrorReporting();
    // Difiere el widget del asistente hasta después del primer render (idle):
    // saca su código del camino crítico de carga de la página.
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const id = idle(() => setWidgetReady(true));
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <SiteFlagsProvider>
            <CountryProvider>
            <HealthBanner />
            <AppRouter />
            <PushBootstrap />
            <Suspense fallback={null}>
              <CookiesBanner />
            </Suspense>
            {widgetReady && (
              <Suspense fallback={null}>
                <FloatingHub />
              </Suspense>
            )}
            <Toaster position="top-right" richColors />
            </CountryProvider>
            </SiteFlagsProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
